import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import filterVisibilityAPI from '../../Network/FilterVisibility'

// Module-level cache so the visibility config is fetched once per page key and
// reused across remounts (e.g. every time the search drawer opens). Without it,
// each mount refetches and filters would flash before the config arrives.
const visibilityCache = {} // pageKey -> { filter_key: 'public'|'private' }

// Hook for list pages: returns isVisible(filterKey) for the given pageKey.
//
// Visibility semantics for a user WITHOUT the show_private_filters role:
//   - while the config is still loading  => hidden (so private filters never
//     "flash" visible before we know they're private)
//   - 'private'                          => hidden
//   - 'public' / unconfigured / load err => visible (fail-open to public)
// Users WITH the role always see every filter.
//
// Call this at the PAGE level (a component that stays mounted) and pass the
// resulting isVisible down, so the config is ready before the drawer opens.
export default function useFilterVisibility(pageKey) {
    const rolesRedux = useSelector(state => state.rolesData.roles)
    const hasPrivateRole = !!rolesRedux?.show_private_filters?.value

    const cached = pageKey ? visibilityCache[pageKey] : undefined
    const [visibilityMap, setVisibilityMap] = useState(cached || {})
    // Already "loaded" if we have a cached map or there's no page to load.
    const [loaded, setLoaded] = useState(!!cached || !pageKey)

    const load = useCallback(async () => {
        if (!pageKey) { setLoaded(true); return }
        try {
            const res = await filterVisibilityAPI.getFilterVisibility({ params: { page_key: pageKey } })
            const rows = res?.data?.data
            const map = {}
            if (Array.isArray(rows)) {
                rows.forEach(r => { if (r?.filter_key) map[r.filter_key] = r.visibility })
            }
            visibilityCache[pageKey] = map
            setVisibilityMap(map)
        } catch (e) {
            // fail open: leave map empty => everything defaults to public
        }
        setLoaded(true)
    }, [pageKey])

    useEffect(() => { load() }, [load])

    const isVisible = useCallback((filterKey) => {
        if (hasPrivateRole) return true        // privileged users see everything
        if (!loaded) return false              // hide until known (prevents flash)
        return visibilityMap[filterKey] !== 'private'
    }, [visibilityMap, hasPrivateRole, loaded])

    return { isVisible, hasPrivateRole, loaded, reload: load }
}
